import { Box, Button, Tag, Text } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  useBookkeepersManagementPayrollResultsDataTable,
} from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateRangeWithYear } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { usePayrollResultFilterContext } from 'modules/bookkeepers/hooks/PayrollResultContext'
import { useIncidenceFilterContext } from 'modules/bookkeepers/lib/IncidenceFilterContext'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'

import PayrollResultsFilter from './Filter'
import { statusModifier, tagColor } from './util'

const PayrollResultsDataTable = () => {
  const { navigation } = useNavigation()
  const { status, searchedCompanyIds } = usePayrollResultFilterContext()
  const statuses: string[] = status.split(',')
  const { selectedCompany: filterContextCompany, filterDate } = useIncidenceFilterContext()
  const selectedCompany = filterContextCompany?.attributes.id

  const renderText = (value: string | null | undefined) => (
    <Text.Normal size="200" color="grey1000" capitalize>
      {value}
    </Text.Normal>
  )

  const isBookkeeperView = useBookkeeperView()
  const source = useBookkeepersManagementPayrollResultsDataTable(
    [
      {
        identifier: 'company',
        kind: ProjectionKind.Custom,
        template: '',
        title: i18n.t('bookkeepers.clients.payroll_dashboard.table_columns.company'),
        render: ({ policy_company_name }) => renderText(policy_company_name),
      },
      {
        identifier: 'name',
        kind: ProjectionKind.Custom,
        template: '',
        title: i18n.t('bookkeepers.clients.payroll_dashboard.table_columns.policy'),
        render: ({ policy_name }) => renderText(policy_name),
      },
      {
        identifier: 'period',
        kind: ProjectionKind.Custom,
        template: '',
        title: i18n.t('bookkeepers.clients.payroll_dashboard.table_columns.period'),
        render: ({ startsOn, endsOn }) =>
          renderText(dateRangeWithYear(String(startsOn), String(endsOn))),
        sortBy: 'startsOn',
      },
      {
        identifier: 'status',
        kind: ProjectionKind.Custom,
        template: '',
        title: i18n.t('bookkeepers.clients.payroll_dashboard.table_columns.status'),
        render: ({ status }) => (
          <Box maxWidth="fit-content">
            <Tag leftItem="circle" type={tagColor(String(status))} showCircle>
              {statusModifier(String(status))}
            </Tag>
          </Box>
        ),
      },
      {
        identifier: 'link',
        kind: ProjectionKind.Decorative,
        render: () => (
          <Box alignItems="flexEnd" paddingY="s8">
            <Button.Decorative type="secondary" iconName="arrow-right" />
          </Box>
        ),
      },
    ],
    {
      variables: {
        companyIds: selectedCompany ? selectedCompany : searchedCompanyIds,
        status: statuses,
        periodEndsOn: filterDate.lastDate,
        periodStartsOn: filterDate.firstDate,
      },
    }
  )

  return (
    <DataTable.Provider
      source={source}
      pagination={{
        kind: 'cursor',
      }}
      rowTo={({ id, policy_id }) =>
        navigation.bookkeepers.clients.payrollResults.payrollPolicy.payrollPolicyPeriodId.fullPath({
          policyId: Number(policy_id),
          periodId: Number(id),
        })
      }
      sort={{
        path: 'startsOn',
        direction: 'desc',
      }}
      queryContext={{ isBookkeeperView }}
    >
      <Box gap="s16">
        <DataTable.HeadBar disableExport toolbar={<PayrollResultsFilter />} />
        <DataTable.Table
          density="compact"
          emptyState={
            <Box>
              <Text.Normal>
                {i18n.t('bookkeepers.clients.payroll_dashboard.no_results_found')}
              </Text.Normal>
            </Box>
          }
        />
      </Box>
    </DataTable.Provider>
  )
}

export default PayrollResultsDataTable
